import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { parseValidHttpsUrl } from 'common/parse-valid-https-url';

const BOOKING_IFRAME_SESSION_KEY = 'medibank-pp-booking-iframe-session';

export type BookingIframeSessionPayload = {
  bookingLink: string;
  /** `pathname + search` so refresh restores only on the same screen. */
  returnPath: string;
};

/**
 * Path (pathname + search) captured when the masonry action fires so the
 * booking iframe deeplink targets the screen the user was on.
 */
export function getBookAppointmentModalTargetPath(): string {
  if (typeof window === 'undefined') {
    return APP_MANAGED_ROUTES.HOME;
  }
  return `${window.location.pathname}${window.location.search}`;
}

export function stripBookingLinkFromLocationState(
  state: unknown,
): Record<string, unknown> | undefined {
  if (state === null || state === undefined || typeof state !== 'object') {
    return undefined;
  }
  const rest = Object.fromEntries(
    Object.entries(state as Record<string, unknown>).filter(
      ([key]) => key !== 'bookingLink',
    ),
  );
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/**
 * Persists an in-progress booking iframe session for the current screen so a
 * full page refresh can reopen the modal. Cleared when the user finishes or
 * leaves booking ({@link clearBookingIframeSession}).
 */
export function persistBookingIframeSession(
  bookingLink: string,
  returnPath: string,
): void {
  const link = parseValidHttpsUrl(bookingLink);
  if (!link || typeof sessionStorage === 'undefined') {
    return;
  }
  const payload: BookingIframeSessionPayload = {
    bookingLink: link,
    returnPath,
  };
  sessionStorage.setItem(BOOKING_IFRAME_SESSION_KEY, JSON.stringify(payload));
}

/**
 * Pure read — returns the stored booking URL only when it matches
 * `currentReturnPath`. Does NOT mutate sessionStorage; call
 * {@link clearStaleBookingIframeSession} in a `useEffect` to evict stale
 * entries without causing render-time side effects.
 */
export function readStoredBookingIframeSession(
  currentReturnPath: string,
): string | undefined {
  if (typeof sessionStorage === 'undefined') {
    return undefined;
  }
  const raw = sessionStorage.getItem(BOOKING_IFRAME_SESSION_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<BookingIframeSessionPayload>;
    const link = parseValidHttpsUrl(parsed.bookingLink);
    if (
      !link ||
      typeof parsed.returnPath !== 'string' ||
      parsed.returnPath !== currentReturnPath
    ) {
      return undefined;
    }
    return link;
  } catch {
    return undefined;
  }
}

/**
 * Removes a stored booking session that is stale (invalid payload or
 * `returnPath` does not match `currentReturnPath`). Intended to be called
 * from a `useEffect` so sessionStorage is never mutated during render.
 */
export function clearStaleBookingIframeSession(
  currentReturnPath: string,
): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const raw = sessionStorage.getItem(BOOKING_IFRAME_SESSION_KEY);
  if (!raw) {
    return;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<BookingIframeSessionPayload>;
    const link = parseValidHttpsUrl(parsed.bookingLink);
    if (
      !link ||
      typeof parsed.returnPath !== 'string' ||
      parsed.returnPath !== currentReturnPath
    ) {
      sessionStorage.removeItem(BOOKING_IFRAME_SESSION_KEY);
    }
  } catch {
    sessionStorage.removeItem(BOOKING_IFRAME_SESSION_KEY);
  }
}

export function clearBookingIframeSession(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(BOOKING_IFRAME_SESSION_KEY);
}
